import { Box } from "@mui/material";
import { getBackendErrors } from "api/errors";
import { useLeaderboards } from "api/leaderboards";
import { useAppSelector } from "app/hooks";
import { FormButtons } from "components/FormControls/FormButtons";
import {
  prepareVisibilityData,
  transformTranslations,
} from "components/Layout/Campaign/Common/campaignHelpers";
import {
  checkTranslationsError,
  updateErrors,
} from "components/Layout/Campaign/Direct/CampaignForm/validationSchema";
import { Stepper } from "components/Stepper/Stepper";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { surveyQuery } from "constants/general";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik, FormikErrors, FormikHelpers } from "formik";
import { useMessage } from "hooks/intl";
import { useStepper } from "hooks/useStepper";
import { isEmpty } from "lodash";
import { useNavigate } from "react-router-dom";
import {
  selectAvaiableTranslations,
  selectDefaultLanguageData,
} from "reduxState/translations/translationsSlice";
import {
  CampaignTriggers,
  CampaignTypes,
  VisibilityTarget,
} from "types/campaigns";
import { LeaderboardBE, LeaderboardFE } from "types/leaderboards/base";
import { Status, StepDetails } from "types/stepper";
import {
  backendErrors,
  parseCampaignTranslationsArrayError,
} from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { Info } from "./Info/Info";
import { useStyles } from "./styles";
import { Summary } from "./Summary/Summary";
import { getTabWithErrors } from "./utils";
import { getValidationSchemaByStep } from "./validationSchema";

interface Props {
  initialValues?: LeaderboardFE;
  leaderboardId?: string;
  isEditMode?: boolean;
  initialErrors?: FormikErrors<LeaderboardBE>;
  onCancelCallback?: () => void;
}

export enum LeaderboardStepsIds {
  INFO = "info",
  SUMMARY = "summary",
}

export type LeaderboardStepsUnion = `${LeaderboardStepsIds}`;

export const leaderboardFormSteps: StepDetails<LeaderboardStepsUnion>[] = [
  {
    id: LeaderboardStepsIds.INFO,
    label: "leaderboardBasicInfo",
    description: "leaderboardBasicDescription",
    status: Status.ACTIVE,
  },
  {
    id: LeaderboardStepsIds.SUMMARY,
    label: "summary",
    description: "reviewConfiguration",
    status: Status.NOT_VISITED,
  },
];

const LeaderboardForm = ({
  initialValues,
  isEditMode = false,
  leaderboardId,
  initialErrors,
  onCancelCallback,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const { addLeaderboard, editLeaderboard } = useLeaderboards();
  const availableLanguages = useAppSelector(selectAvaiableTranslations);
  const defaultLang = useAppSelector(selectDefaultLanguageData);

  const {
    steps,
    isLastStep,
    isFirstStep,
    activeStep,
    setSteps,
    nextStep,
    prevStep,
    setStepError,
    skipToStep,
    setStepsErrors,
  } = useStepper(leaderboardFormSteps);

  const handleSubmit = async (
    values: any,
    { setErrors, setSubmitting }: FormikHelpers<any>,
  ) => {
    if (!isLastStep) {
      nextStep();
      return;
    }

    setSubmitting(true);

    // Format values for submission
    const properties = {
      type: CampaignTypes.DIRECT,
      trigger: CampaignTriggers.LEADERBOARD,
      visibility:
        values?.visibility?.target === VisibilityTarget.ALL
          ? undefined
          : prepareVisibilityData(values?.visibility),
      translations: transformTranslations(values?.translations),
      activity: values.activity,
      labels: values.labels,
      active: values.active,
      limits: values.limits,
    };

    try {
      isEditMode && leaderboardId
        ? await editLeaderboard(leaderboardId, properties)
        : await addLeaderboard(properties);

      notify(
        message({
          id: isEditMode ? "leaderboardUpdated" : "leaderboardCreated",
        }),
      );

      return navigate(
        isEditMode
          ? `${links.leaderboardsList}`
          : `${links.leaderboardsList}${surveyQuery}`,
      );
    } catch (exception) {
      const errors = backendErrors(getBackendErrors(exception));

      const parsedErrors = {
        ...errors,
        ...(errors?.translations
          ? {
              translations: parseCampaignTranslationsArrayError(
                errors?.translations,
                values?.translations,
              ),
            }
          : {}),
      };
      setErrors(parsedErrors);
      focusError();
      const tabWithErrors = getTabWithErrors(parsedErrors);
      setStepsErrors(tabWithErrors);
    }
  };

  const defaultValues = {
    translations: [
      defaultLang && {
        description: "",
        locale: defaultLang.code,
        name: "",
      },
    ],
    activity: {
      startsAt: null,
      endsAt: null,
    },
    labels: [],
    active: false,
    visibility: { target: VisibilityTarget.ALL },
    type: CampaignTypes.DIRECT,
    trigger: CampaignTriggers.LEADERBOARD,
  };

  const validationSchema = getValidationSchemaByStep(activeStep.id, message);
  const nextStepInfo = isLastStep
    ? activeStep
    : steps[steps.findIndex((step) => step.id === activeStep.id) + 1];

  return (
    <Formik
      initialValues={initialValues || defaultValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema}
      initialErrors={initialErrors}
    >
      {({
        isSubmitting,
        errors,
        dirty,
        handleSubmit,
        values,
        validateForm,
        setErrors,
        initialValues,
      }) => {
        const { translations, labels } = values;
        const validateStep = async () => {
          const stepErrors = await validateForm();
          const currentStepFields = Object.keys(validationSchema?.fields || {});
          const areStepErrors = Object.keys(stepErrors).length > 0;
          const mergedErrors = updateErrors(
            errors,
            stepErrors,
            currentStepFields,
          );

          setErrors(mergedErrors);

          if (areStepErrors) return false;

          setStepError(activeStep.id, false);

          return true;
        };

        return (
          <>
            {!isSubmitting && (
              <LeaveSiteGuard
                warnBeforeRefresh
                isDirty={dirty}
                initialValues={initialValues}
                currentValues={values}
              />
            )}
            <div className={classes.content}>
              <Box className={classes.stepper}>
                <Box className={classes.contentWrapper}>
                  <div className={classes.stepperWrapper}>
                    <Stepper
                      steps={steps}
                      setSteps={async (
                        stepsDetails: StepDetails<LeaderboardStepsUnion>[],
                      ) => {
                        const isStepValid = await validateStep();

                        return setSteps(
                          stepsDetails.map((step) => ({
                            ...step,
                            hasError:
                              step.id === activeStep.id
                                ? !isStepValid
                                : step.hasError,
                          })),
                        );
                      }}
                    />
                  </div>
                </Box>
                <div className={classes.separator} />
              </Box>
              <Form
                onSubmit={handleSubmit}
                className={`${classes.contentWrapper} ${classes.form}`}
              >
                {activeStep.id === LeaderboardStepsIds.INFO && (
                  <Info
                    availableLanguages={availableLanguages}
                    translations={translations}
                    labels={labels}
                    sectionErrors={{
                      title: checkTranslationsError(errors?.translations),
                      startEndDate: !isEmpty(errors?.activity),
                      visibility: !isEmpty(errors?.visibility),
                      attributes: !isEmpty(errors?.labels),
                    }}
                    isEditMode={isEditMode}
                  />
                )}
                {activeStep.id === LeaderboardStepsIds.SUMMARY && (
                  <Summary
                    values={values}
                    defaultLang={defaultLang}
                    skipToStep={skipToStep}
                    errors={errors}
                  />
                )}
              </Form>
            </div>
            <footer className={classes.footer}>
              <div className={classes.contentWrapper}>
                <FormButtons
                  className={classes.submitWrapper}
                  disableSubmit={isSubmitting}
                  onCancelClick={async () => {
                    if (isFirstStep) {
                      if (onCancelCallback) {
                        return onCancelCallback();
                      }
                      return navigate(links.campaignsList);
                    }
                    const isStepValid = await validateStep();
                    if (!isStepValid) setStepError(activeStep.id, true);

                    prevStep();
                  }}
                  onSubmitClick={async () => {
                    if (isLastStep) handleSubmit();
                    const isStepValid = await validateStep();

                    if (!isStepValid) {
                      setStepError(activeStep.id, true);
                    }
                    nextStep();
                  }}
                  cancelMessage={isFirstStep ? "cancel" : "previousStep"}
                  submitMessage={
                    !isLastStep
                      ? `${message({ id: "nextStep" })}: ${message({ id: nextStepInfo?.label })}`
                      : isEditMode
                        ? "saveChanges"
                        : "addLeaderboard"
                  }
                />
              </div>
            </footer>
          </>
        );
      }}
    </Formik>
  );
};

export default LeaderboardForm;
